import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles, Rocket, Paintbrush, Wrench, Bug, ShieldCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import authedFetch from '../../lib/authedFetch';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'lie.whatsNew.lastSeenDate';

const CATEGORY_META = {
  engineering: { icon: Wrench, chip: 'bg-indigo-500/15 text-indigo-300 ring-indigo-400/30' },
  ux: { icon: Paintbrush, chip: 'bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-400/30' },
  feature: { icon: Rocket, chip: 'bg-teal-500/15 text-teal-300 ring-teal-400/30' },
  fix: { icon: Bug, chip: 'bg-amber-500/15 text-amber-300 ring-amber-400/30' },
  security: { icon: ShieldCheck, chip: 'bg-rose-500/15 text-rose-300 ring-rose-400/30' },
};

function getCategoryMeta(category) {
  return CATEGORY_META[category] || CATEGORY_META.feature;
}

export default function WhatsNewBadge({ isCollapsed }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSeen, setLastSeen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const fetchNotes = useCallback(async () => {
    try {
      const res = await authedFetch(`${API}/api/release-notes?limit=10`);
      if (!res.ok) {
        setItems([]);
        return;
      }
      const data = await res.json();
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const unseenCount = useMemo(
    () => items.filter((r) => !lastSeen || (r.date && r.date > lastSeen)).length,
    [items, lastSeen]
  );

  useEffect(() => {
    if (open && unseenCount > 0) {
      const newest = items.reduce((acc, r) => (r.date && r.date > acc ? r.date : acc), '');
      if (newest) {
        try {
          localStorage.setItem(STORAGE_KEY, newest);
        } catch {
          // ignore quota / disabled storage
        }
        setLastSeen(newest);
      }
    }
  }, [open, unseenCount, items]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="What's new"
          data-testid="whats-new-trigger"
          className={cn(
            'relative flex items-center gap-2 rounded-md text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900',
            isCollapsed ? 'mx-auto h-10 w-10 justify-center' : 'w-full px-3 py-2'
          )}
        >
          <Sparkles className="h-4 w-4 flex-shrink-0 text-amber-500" />
          {!isCollapsed && (
            <span className="text-sm font-medium">What's new</span>
          )}
          {unseenCount > 0 && (
            <span
              className={cn(
                'absolute flex h-2.5 w-2.5',
                isCollapsed ? 'right-1.5 top-1.5' : 'right-3 top-2.5'
              )}
              data-testid="whats-new-pulse"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-white" />
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="start"
        sideOffset={12}
        className="w-80 border-slate-200 bg-white p-0 text-slate-700 shadow-xl"
        data-testid="whats-new-popover"
      >
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-semibold tracking-wide text-slate-900">Recently shipped</h3>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            The latest improvements to your Seertech.
          </p>
        </div>

        {loading ? (
          <div className="px-4 py-6 text-center text-xs text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-slate-500">No updates yet.</div>
        ) : (
          <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
            {items.map((r) => {
              const { icon: Icon, chip } = getCategoryMeta(r.category);
              const isNew = !lastSeen || (r.date && r.date > lastSeen);
              return (
                <li
                  key={r.id}
                  className="px-4 py-3"
                  data-testid={`whats-new-item-${r.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={cn(
                        'mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ring-1',
                        chip
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium text-slate-900">{r.title}</p>
                        {isNew && (
                          <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200">
                            New
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-600">{r.body}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                        {r.date
                          ? new Date(r.date).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : ''}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
